import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useApi, useDirect } from "./index";
import { apigetDropdownFiltersData, method } from "../apiPath";
import keyNames from "../keyName";

/**
 * Dynamically fetches dependent dropdown options via the common useApi hook
 * and reads results from the Redux store (keyNames.dynamicDropdownOptions).
 *
 * Hierarchies handled:
 *   Country  → Region, State, City
 *   Industry → Sub Industry
 *   Job Function → Job Seniority
 *
 * Returns null when no parent selection is active — callers should fall back
 * to the base (unfiltered) options from Redux in that case.
 */
const useGeoOptions = (
  campaignId,
  selectedCountries    = [],
  selectedRegions      = [],
  selectedStates       = [],
  selectedIndustries   = [],
  selectedJobFunctions = []
) => {
  const hitApi       = useApi();
  const directDispatch = useDirect();
  const lastKeyRef   = useRef("");

  const dynamicOptions = useSelector(
    (state) => state.mainReducer[keyNames.dynamicDropdownOptions]
  );

  // Stable string keys so the effect uses primitive deps
  const countriesKey    = selectedCountries.join("|");
  const regionsKey      = selectedRegions.join("|");
  const statesKey       = selectedStates.join("|");
  const industriesKey   = selectedIndustries.join("|");
  const jobFunctionsKey = selectedJobFunctions.join("|");

  useEffect(() => {
    const countries    = countriesKey    ? countriesKey.split("|").filter(Boolean)    : [];
    const regions      = regionsKey      ? regionsKey.split("|").filter(Boolean)      : [];
    const states       = statesKey       ? statesKey.split("|").filter(Boolean)       : [];
    const industries   = industriesKey   ? industriesKey.split("|").filter(Boolean)   : [];
    const jobFunctions = jobFunctionsKey ? jobFunctionsKey.split("|").filter(Boolean) : [];

    const hasSelection =
      countries.length > 0  ||
      regions.length > 0    ||
      states.length > 0     ||
      industries.length > 0 ||
      jobFunctions.length > 0;

    if (!hasSelection) {
      directDispatch(null, keyNames.dynamicDropdownOptions);
      lastKeyRef.current = "";
      return;
    }

    const params = new URLSearchParams();
    if (campaignId) params.append("campaignId", campaignId);
    countries.forEach((c)    => params.append("country",     c));
    regions.forEach((r)      => params.append("region",      r));
    states.forEach((s)       => params.append("state",       s));
    industries.forEach((i)   => params.append("industry",    i));
    jobFunctions.forEach((j) => params.append("jobFunction", j));

    const key = params.toString();
    if (key === lastKeyRef.current) return; // identical request — skip
    lastKeyRef.current = key;

    hitApi(
      `${apigetDropdownFiltersData}?${key}`,
      null,
      method.get,
      keyNames.dynamicDropdownOptions
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, countriesKey, regionsKey, statesKey, industriesKey, jobFunctionsKey]);

  // Reset store entry when campaign switches
  useEffect(() => {
    directDispatch(null, keyNames.dynamicDropdownOptions);
    lastKeyRef.current = "";
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  return dynamicOptions;
};

export default useGeoOptions;
